/**
 * The poster's palettes.
 *
 * Two rules shape all of them. Colour is only ever carried by text, borders and
 * decorative blocks — never by text sitting on a coloured fill — so a printer
 * that drops backgrounds still hands back a readable sheet. And the code itself
 * keeps its own light card whatever the sheet is: a scanner wants dark modules
 * on a light ground, and inverting that is how a QR stops being read.
 */

export type QrPosterThemeId = 'lime' | 'ink' | 'mono';

export interface QrPosterTheme {
  /** The sheet, and the paper it bleeds to. */
  sheet: string;
  /** The block the name sits in. */
  headerBg: string;
  /** Rules, numbers, icons — the one colour the poster spends. */
  accent: string;
  /** Headings. */
  ink: string;
  /** Running text. */
  body: string;
  /** Addresses, the URL, the tagline. */
  muted: string;
  /** Hairlines and card borders. */
  line: string;
  /** The card the code sits on: light in every theme, deliberately. */
  qrCard: string;
  /** The code's own modules. */
  qrFg: string;
  /** The dashed lines the copies are cut apart along. */
  cut: string;
  /**
   * Applied to the DivePeaks mark, which is a fixed green SVG. Only the
   * black-and-white theme has anything to say about it.
   */
  markFilter?: string;
}

export const QR_POSTER_THEMES: Record<QrPosterThemeId, QrPosterTheme> = {
  /** The brand: the DivePeaks mark's own green on white. */
  lime: {
    sheet: '#ffffff',
    headerBg: '#F7FAEF',
    accent: '#93C92B',
    ink: '#111827',
    body: '#4B5563',
    muted: '#6B7280',
    line: '#E5E7EB',
    qrCard: '#ffffff',
    qrFg: '#111827',
    cut: '#B6BDC6',
  },
  /** Dark: for a wall where a white sheet disappears. Thirsty on toner. */
  ink: {
    sheet: '#111827',
    headerBg: '#0B1220',
    // Lifted off the brand green, which is too dark to read on this ground.
    accent: '#A9DE3D',
    ink: '#ffffff',
    body: '#D5DAE3',
    muted: '#9CA3AF',
    line: '#2D3748',
    qrCard: '#ffffff',
    qrFg: '#111827',
    cut: '#6B7280',
  },
  /** No colour at all, for the office laser that only has black. */
  mono: {
    sheet: '#ffffff',
    headerBg: '#F4F4F5',
    accent: '#111827',
    ink: '#111827',
    body: '#3F3F46',
    muted: '#71717A',
    line: '#D4D4D8',
    qrCard: '#ffffff',
    qrFg: '#000000',
    cut: '#A1A1AA',
    markFilter: 'grayscale(1) contrast(1.3) brightness(0.55)',
  },
};
